/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/BoardCard" />

import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { ObservableCollection, ObservableValue, useSubscription } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { IMenuItem, MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { KeyCode, css } from "VSSUI/Util";
import { BoardAnnotationsIdentifiers } from "Wit/BoardsHub/Common/AnnotationUtils";
import { IBoardsWorkItemService } from "Wit/BoardsHub/Common/IBoardsWorkItemService";
import { IBoardsWorkItem, IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import {
    BoardColumnLayout,
    BoardRowLayout,
    CardLayout,
    IAnnotationSetting,
    WorkItemTypeColorAndIconMap
} from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { CardMenuButton } from "Wit/BoardsHub/KanbanBoard/Components/CardMenuButton";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { getWorkItemUrl } from "Wit/BoardsHub/KanbanBoard/Utils/RoutingUtils";
import { DraggableCard, IDraggableCardProps } from "Wit/Common/Controls/Boards/Card";
import { DragMode, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { OnChangeType } from "Wit/Common/Controls/Boards/EditableField";
import { ValidationError } from "Wit/Common/Controls/Boards/ValidationError";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IHotKeyEventData, IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import * as React from "react";
import { useState } from "react";
import { IAnnotationService } from "../IAnnotationService";
import { IKanbanBoardService } from "../IKanbanBoardService";
import { focusCard } from "../Utils/BoardUtils";
import { Annotations } from "./Annotations/Annotations";
import { addContributedMenuItems, getDefaultMenuItems, getDeleteDialog, getDeleteMenuItem, onWorkItemClick, openWorkItemForm } from "./Util";

interface IBoardCardProps extends Required<Pick<IDraggableCardProps<IBoardsWorkItem, IObservableBoardsWorkItem>, "onKeyDown">> {
    cardLayouts: { [type: string]: CardLayout };
    className?: string;
    item: IObservableBoardsWorkItem;
    onCardDrag: (e: PointerEvent, draggingElement: HTMLElement, mode: DragMode, position: IPosition) => undefined;
    pageContext: IVssPageContext;
    tagStyles?: Map<string, string>;
    workItemTypeColorAndIconsMap: WorkItemTypeColorAndIconMap;
    columnName: string;
    columnIsSplited?: boolean;
    columnIsDone?: boolean;
    rowName?: string;
    columnChanged: ObservableValue<boolean>;
    doneChanged?: ObservableValue<boolean>;
    rowChanged?: ObservableValue<boolean>;
    scrollableData?: ScrollableData;
    onFocus?: () => void;
}

export function BoardCard({
    cardLayouts,
    className,
    item,
    onCardDrag,
    onKeyDown,
    pageContext,
    tagStyles,
    workItemTypeColorAndIconsMap,
    columnName,
    columnIsDone,
    columnIsSplited,
    rowName,
    columnChanged,
    doneChanged,
    rowChanged,
    scrollableData,
    onFocus
}: IBoardCardProps) {
    // The work item ID can change without the work item object changing (i.e. when a new work item is saved) and we need to re-render when this happens.
    const [itemId, setItemId] = useState(item.value.id);
    useSubscription(
        item,
        wi => {
            if (wi.id !== itemId) {
                setItemId(wi.id);
            }
        },
        [itemId]
    );

    const type = item.value.getRequiredFieldValue<string>(CoreFieldRefNames.WorkItemType);
    const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
    const workItemService = pageContext.getService<IBoardsWorkItemService>("IBoardsWorkItemService");
    const annotationService = pageContext.getService<IAnnotationService>("IAnnotationService");
    const onDeleteItem = (itemId: number) => {
        boardService.removeItem(itemId);
        workItemService.updateParentFieldTitle(itemId, undefined, /** parentDeleted */ true);
    };
    const onDismissItem = (itemId: number) => {
        if (boardService.boardSettingsData.value?.annotationSettings) {
            annotationService.getAnnotationsForItemsAsync(
                [itemId],
                boardService.boardSettingsData.value.boardData.boardModel.boardSettings.teamId,
                boardService.boardSettingsData.value?.annotationSettings,
                boardService.boardSettingsData.value?.orderField?.referenceName
            );
        }
    };

    const clickHandler = React.useCallback(
        (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, workItemId: number) =>
            onWorkItemClick(pageContext, e, workItemId, undefined, undefined, onDeleteItem, onDismissItem),
        [pageContext]
    );

    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");

    const deleteHotKeyCallback = ({ workItemId, projectName }: IHotKeyEventData) => {
        if (workItemId && workItemId == itemId) {
            const onDeleteItem = () => boardService.removeItem(workItemId);

            getDeleteDialog(pageContext, workItemId, projectName ?? "", onDeleteItem);
        }
    };

    React.useEffect(() => {
        hotKeyEventService.subscribe(deleteHotKeyCallback, IHotKeyEventType.WorkItemDelete);
        return () => hotKeyEventService.unsubscribe(deleteHotKeyCallback, IHotKeyEventType.WorkItemDelete);
    }, [itemId]);

    const menuButtonRef = React.useRef<MenuButton | null>(null);
    const onRenderMenuButton = React.useCallback(
        (pageContext: IVssPageContext, item: IObservableBoardsWorkItem) => (
            <BoardMenuButton
                currentItem={item}
                menuButtonRef={menuButtonRef}
                hasPermissionToDeleteWorkItem={boardService.hasPermissionToDeleteWorkItems()}
            />
        ),
        [menuButtonRef]
    );
    const onKeyDownCallback = React.useCallback(
        (e: React.KeyboardEvent<HTMLElement>, item: IObservableBoardsWorkItem) => {
            if (e.keyCode === KeyCode.F10 && e.shiftKey) {
                menuButtonRef.current?.expand();
                e.preventDefault();
            } else {
                onKeyDown(e, item);
            }
        },
        [onKeyDown]
    );

    const layout = cardLayouts[type];
    const memodItemLayout = React.useMemo(
        () => ({
            workItem: item,
            coreFields: layout.coreFields,
            additionalFields: layout.additionalFields,
            url: getWorkItemUrl(pageContext, itemId),
            workItemTypeColorAndIcon: workItemTypeColorAndIconsMap[type]!
        }),
        [item, itemId, layout, workItemTypeColorAndIconsMap]
    );

    return (
        <DraggableCard
            key={itemId}
            cardFieldsCollapsed={false}
            className={css("boards-card body-s", className)}
            itemLayout={memodItemLayout}
            showChildRollup={false}
            showEmptyFields={layout.showEmptyFields}
            compact={false}
            full={false}
            isTitleEditing={item.value.isTitleEditing}
            isMoveDisabled={() => !!item.value.iWorkItem.value?.getError()}
            onDrag={onCardDrag}
            onDragComplete={onCardDrag}
            onChange={onChange}
            onKeyDown={onKeyDownCallback}
            onFocus={onFocus}
            onRenderAdditionalContent={renderAdditionalContent}
            onRenderMenuButton={onRenderMenuButton}
            onWorkItemClick={clickHandler}
            resizeDisabled={true}
            tagStyles={tagStyles}
            columnName={columnName}
            columnIsSplited={columnIsSplited}
            columnIsDone={columnIsDone}
            rowName={rowName}
            columnChanged={columnChanged}
            rowChanged={rowChanged}
            scrollableData={scrollableData}
            doneChanged={doneChanged}
            wrapTags
        />
    );
}

function renderAdditionalContent(observableItem: IObservableBoardsWorkItem): JSX.Element {
    return (
        <>
            <Annotations item={observableItem} />
            <ValidationError observableItem={observableItem} onOpenWorkItemForm={openWorkItemForm} />
        </>
    );
}

const onChange: OnChangeType = (pageContext, item, fieldReferenceName, newValue) => {
    const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
    boardService.updateItem(item.id, fieldReferenceName, newValue);
};

function getContextMenuItems(
    menuItems: ObservableCollection<IMenuItem>,
    pageContext: IVssPageContext,
    currentItem: IObservableBoardsWorkItem,
    teamSettings: ITeamSettings | undefined,
    hasPermissionToDeleteWorkItem: boolean
) {
    const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
    const updateIterations = (index: number, iteration: IIterationData) => {
        boardService.updateItemIteration(currentItem, iteration);
    };

    const workItemTypeColorAndIconMap = boardService.boardLayout.value?.workItemTypeColorAndIconsMap;
    const iterationItems = WorkItemUtils.setIterationItems(teamSettings, updateIterations);
    const projectName = currentItem.value.getFieldValue<string>(CoreFieldRefNames.TeamProject) ?? "";
    const workItemType = currentItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType) ?? "";
    const id = currentItem.value.id;

    const getSelectedItem = () => {
        return getSelectedItems(currentItem);
    };

    const onEditTitle = () => {
        currentItem.value.isTitleEditing.value = true;
    };

    const onDeleteItem = () => boardService.removeItem(currentItem.value.id);
    const annotationSettings = boardService.boardSettingsData.value?.annotationSettings;

    const column = boardService.getWorkItemColumnLayout(currentItem.value.id);
    const row = boardService.getWorkItemRowLayout(currentItem.value.id);
    const canReorder = column && boardService.canReorderInColumn(column.id);
    const onMoveToPosition = (index: number) => {
        if (!column) {
            return;
        }

        const wiPosition = column.workItems.value.findIndex(wi => wi.value.id === currentItem.value.id);
        if (wiPosition === index) {
            return;
        }

        index = wiPosition > index ? index - 1 : index;
        const previousId = column.workItems.value[index]?.value.id;
        const nextId = column.workItems.value[index + 1]?.value.id;
        if (previousId || nextId) {
            boardService.moveItem(currentItem.value.id, column.id, column.rowId, column.isDone, previousId, nextId);
        }
    };

    const columnsItems = getColumnsMenuItems(pageContext, currentItem.value, column?.id, row?.id, column?.isDone).value;
    const rowsItems = getRowsMenuItems(pageContext, currentItem.value, column?.id, row?.id, column?.isDone).value;

    menuItems.push(getDefaultMenuItems(pageContext, id, onEditTitle).value);
    menuItems.push([
        {
            id: "move-to-column",
            rank: 30,
            iconProps: { ariaHidden: true, iconName: "Switch" },
            groupKey: "manage-work-item",
            text: Resources.MoveToColumn,
            subMenuProps: { id: "columns-sub-menu-items", items: columnsItems }
        }
    ]);
    // There is always 1 default swimlane, so we need to check if length > existing default
    if (rowsItems && rowsItems.length > 1) {
        menuItems.push([
            {
                id: "move-to-swimlane",
                rank: 30,
                iconProps: { ariaHidden: true, iconName: "SwitcherStartEnd" },
                groupKey: "manage-work-item",
                text: Resources.MoveToSwimlane,
                subMenuProps: { id: "rows-sub-menu-items", items: rowsItems }
            }
        ]);
    }
    menuItems.push([
        {
            id: "move-to-iteration",
            rank: 30,
            groupKey: "manage-work-item",
            text: Resources.MoveToIteration,
            subMenuProps: { id: "iteration-sub-menu-items", items: iterationItems }
        },
        WorkItemUtils.getNewBranchCommandMenuItem(pageContext, getSelectedItem),
        ...getAddAnnotationMenuItems(currentItem, annotationSettings, workItemTypeColorAndIconMap)
    ]);
    hasPermissionToDeleteWorkItem && menuItems.push([getDeleteMenuItem(pageContext, id, projectName, onDeleteItem)]);
    addContributedMenuItems(pageContext, menuItems, id, workItemType);
    if (canReorder && column.workItems.length > 1 && !currentItem.value.iWorkItem.value?.getError()) {
        menuItems.push([WorkItemUtils.getMoveToPositionDialog(pageContext, column.workItems.value, onMoveToPosition, 31)]);
    }
}

function getColumnsMenuItems(
    pageContext: IVssPageContext,
    workItem: IBoardsWorkItem,
    currentColumnId: string | undefined,
    currentRowId: string | undefined,
    currentColumnIsDone: boolean | undefined
): ObservableCollection<IMenuItem> {
    const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
    const columnsLayout = boardService.getColumnsLayout();
    const menuItems = new ObservableCollection<IMenuItem>();
    enum ColumnTypes { NonSplited, Doing, Done };

    const createWorkItemMenuItem = (column: BoardColumnLayout, columnType: ColumnTypes) => {
        const splitColumnsAdditionalText = columnType === ColumnTypes.Done ? " - " + Resources.SplittedColumnDonePostfixName : " - " + Resources.SplittedColumnDoingPostfixName;

        return {
            id: "move-to-column-" + column.name + (column.isSplit && columnType === ColumnTypes.Done ? "-done" : ""),
            groupKey: "manage-work-item",
            text: column.name + (column.isSplit ? splitColumnsAdditionalText : ""),
            rank: 30,
            onActivate: async () => {
                await workItem?.hydrate(pageContext);
                await boardService.moveItem(workItem.id, column.id, currentRowId, columnType === ColumnTypes.Done);
                focusCard(workItem.id);
            }
        };
    }
    columnsLayout?.forEach((column: BoardColumnLayout) => {
        if (currentColumnId === column.id) {
            if (column.isSplit) {
                menuItems.push([createWorkItemMenuItem(column, currentColumnIsDone ? ColumnTypes.Doing : ColumnTypes.Done)]);
            }
        } else if (column.isSplit) {
            menuItems.push([createWorkItemMenuItem(column, ColumnTypes.Doing)]);
            menuItems.push([createWorkItemMenuItem(column, ColumnTypes.Done)]);
        } else {
            menuItems.push([createWorkItemMenuItem(column, ColumnTypes.NonSplited)]);
        }
    });
    return menuItems;
}

function getRowsMenuItems(
    pageContext: IVssPageContext,
    workItem: IBoardsWorkItem,
    currentColumnId: string | undefined,
    currentRowId: string | undefined,
    currentColumnIsDone: boolean | undefined
): ObservableCollection<IMenuItem> {
    const boardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
    const rowsLayout = boardService.getRowsLayout(currentColumnId);
    const menuItems = new ObservableCollection<IMenuItem>();
    rowsLayout?.forEach((row: BoardRowLayout) => {
        menuItems.push([
            {
                id: "move-to-row-" + row.name,
                groupKey: "manage-work-item",
                text: row.name || Resources.DefaultSwimlaneName,
                rank: 30,
                onActivate: async () => {
                    // Exclude move to the same swimlane
                    if ((currentRowId && currentRowId === row.id) || (!currentRowId && row.isDefault)) {
                        return;
                    }
                    if (row && workItem && currentColumnId) {
                        await workItem?.hydrate(pageContext);
                        await boardService.moveItem(workItem.id, currentColumnId, row.id, currentColumnIsDone);
                        focusCard(workItem.id);
                    }
                }
            }
        ]);
    });
    return menuItems;
}

function getAddAnnotationMenuItems(
    currentItem: IObservableBoardsWorkItem,
    annotationSettings?: IAnnotationSetting[],
    workItemTypeColorAndIconMap?: WorkItemTypeColorAndIconMap
): IMenuItem[] {
    if (!annotationSettings) {
        return [];
    }

    const checklistAnnotations = annotationSettings.filter(
        setting =>
            setting.isEnabled &&
            setting.id !== BoardAnnotationsIdentifiers.GitHubLinksAnnotation &&
            setting.id !== BoardAnnotationsIdentifiers.TestAnnotation &&
            !workItemTypeColorAndIconMap?.[setting.name]?.isDisabled
    );
    const menuItems = checklistAnnotations.map(setting => getAddAnnotationMenuItem(currentItem, setting.name));
    const testSetting = annotationSettings.find(setting => setting.isEnabled && setting.id === BoardAnnotationsIdentifiers.TestAnnotation);
    if (testSetting) {
        menuItems.push(getAddAnnotationMenuItem(currentItem, testSetting.name, Resources.AddTest));
    }

    return menuItems;
}

function getAddAnnotationMenuItem(currentItem: IObservableBoardsWorkItem, workItemType: string, label?: string): IMenuItem {
    return {
        id: `add-${workItemType}`,
        rank: 35,
        groupKey: "manage-work-item",
        iconProps: { ariaHidden: true, iconName: "Add" },
        onActivate: () => {
            currentItem.setOpenAnnotation(workItemType, /** adding */ true);
        },
        text: label ?? format(Resources.AddItemButtonLabel, workItemType)
    };
}

function getSelectedItems(currentItem: IObservableBoardsWorkItem) {
    return Array(currentItem.value.getFields());
}

interface IBoardMenuButtonProps {
    currentItem: IObservableBoardsWorkItem;
    menuButtonRef: React.MutableRefObject<MenuButton | null>;
    hasPermissionToDeleteWorkItem: ObservableValue<boolean>;
}

function BoardMenuButton({ currentItem, menuButtonRef, hasPermissionToDeleteWorkItem }: IBoardMenuButtonProps) {
    const pageContext = usePageContext();
    const menuItems = React.useRef<ObservableCollection<IMenuItem>>(new ObservableCollection<IMenuItem>());
    const teamSettingsSvc = pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
    const isExpanded = React.useRef(new ObservableValue<boolean>(false)).current;
    return (
        <Observer
            teamSettings={{
                observableValue: teamSettingsSvc.getTeamSettingsObservable(),
                filter: (value: ITeamSettings | undefined) => {
                    menuItems.current = new ObservableCollection<IMenuItem>();
                    return true;
                }
            }}
            hasPermissionToDeleteWorkItem={hasPermissionToDeleteWorkItem}
            isExpanded={isExpanded}
        >
            {(observerProps: { teamSettings: ITeamSettings | undefined; hasPermissionToDeleteWorkItem: boolean }) => (
                <CardMenuButton
                    className={css("card-context-menu", isExpanded.value && "force-visible")}
                    onExpand={() => {
                        if (menuItems.current.length === 0) {
                            getContextMenuItems(
                                menuItems.current,
                                pageContext,
                                currentItem,
                                observerProps.teamSettings,
                                observerProps.hasPermissionToDeleteWorkItem
                            );
                        }
                        isExpanded.value = true;
                    }}
                    onCollapse={() => {
                        isExpanded.value = false;
                    }}
                    menuItems={menuItems.current}
                    ref={menuButtonRef}
                />
            )}
        </Observer>
    );
}
